import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/cn";
import { useAuth, type Role } from "@/lib/useAuth";
import { PdfGenerator } from "@/pages/PdfGenerator";
import { Glossary } from "@/pages/Glossary";
import { Audit } from "@/pages/Audit";

type TabId = "pdf" | "glossary" | "audit";

interface TabSpec {
  id: TabId;
  label: string;
  icon: string;
  description: string;
  phase: string;
  minRole: Role;
}

const TABS: TabSpec[] = [
  {
    id: "pdf",
    label: "Datenblatt-Generator",
    icon: "fa-file-pdf",
    description: "Produkt + Sprache wählen, Live-Vorschau, PDF-Download.",
    phase: "Phase 6",
    minRole: "viewer",
  },
  {
    id: "glossary",
    label: "Glossar",
    icon: "fa-book",
    description: "Marken/Chemie/Normen: nie übersetzen oder fixe Übersetzungen.",
    phase: "Phase 7",
    minRole: "admin",
  },
  {
    id: "audit",
    label: "Audit-Log",
    icon: "fa-clock-rotate-left",
    description: "Mutationen + PDF-Exports nachvollziehbar.",
    phase: "Phase 8",
    minRole: "admin",
  },
];

interface HealthResponse {
  status: string;
  env: string;
  timestamp: string;
  checks: { oidc: boolean; anthropic: boolean };
}

export function App() {
  const auth = useAuth();
  const [tab, setTab] = useState<TabId>("pdf");
  const [dark, setDark] = useState(false);
  const [about, setAbout] = useState(false);
  const [health, setHealth] = useState<HealthResponse | null>(null);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  useEffect(() => {
    fetch("/api/health")
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(setHealth)
      .catch(() => setHealth(null));
  }, []);

  const visibleTabs = useMemo(() => TABS.filter((t) => auth.has(t.minRole)), [auth]);
  const current = TABS.find((t) => t.id === tab) ?? TABS[0]!;

  // Unangemeldet → vollflächiger Login-Screen, kein Header/Footer/Container.
  if (!auth.loading && !auth.user) {
    return (
      <>
        <LoginScreen onLogin={auth.login} onAbout={() => setAbout(true)} />
        {about && <AboutModal onClose={() => setAbout(false)} />}
      </>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border bg-card/50 backdrop-blur sticky top-0 z-10">
        <div className="container flex h-14 items-center gap-6">
          <div className="flex items-center gap-2 font-semibold">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground text-sm">
              <i className="fa-solid fa-database" />
            </span>
            <span>Fresenius Data-Tool</span>
          </div>
          {auth.user && (
            <nav className="flex gap-1 text-sm">
              {visibleTabs.map((t) => (
                <TabButton
                  key={t.id}
                  active={t.id === tab}
                  icon={t.icon}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                </TabButton>
              ))}
            </nav>
          )}
          <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
            <HealthDot health={health} />
            <button
              type="button"
              onClick={() => setDark((d) => !d)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-secondary"
              title={dark ? "Light mode" : "Dark mode"}
            >
              <i className={cn("fa-solid", dark ? "fa-sun" : "fa-moon")} />
            </button>
            <UserBadge
              loading={auth.loading}
              user={auth.user}
              onLogin={auth.login}
              onLogout={auth.logout}
            />
          </div>
        </div>
      </header>
      <main className="container flex-1 py-8">
        {tab === "pdf" ? (
          <PdfGenerator />
        ) : tab === "glossary" ? (
          <Glossary />
        ) : tab === "audit" ? (
          <Audit />
        ) : (
          <PhasePlaceholder spec={current} />
        )}
      </main>
      <footer className="border-t border-border text-xs text-muted-foreground">
        <div className="container py-3 flex items-center justify-between">
          <span>data.freseniusinstruments.com</span>
          <div className="flex items-center gap-4">
            <span>Build {new Date().toISOString().slice(0, 10)}</span>
            <button
              type="button"
              onClick={() => setAbout(true)}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-border text-[10px] hover:bg-secondary"
              aria-label="About"
              title="About"
            >
              <i className="fa-solid fa-info" />
            </button>
          </div>
        </div>
      </footer>
      {about && <AboutModal onClose={() => setAbout(false)} />}
    </div>
  );
}

function TabButton({
  active,
  icon,
  onClick,
  children,
}: {
  active: boolean;
  icon: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-3 py-1.5 rounded-md font-medium transition-colors text-sm",
        active
          ? "bg-secondary text-secondary-foreground"
          : "text-muted-foreground hover:text-foreground hover:bg-secondary/50",
      )}
    >
      <i className={cn("fa-solid", icon, "mr-1.5 text-xs")} />
      {children}
    </button>
  );
}

function HealthDot({ health }: { health: HealthResponse | null }) {
  if (!health) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full bg-destructive" />
        <span>API offline</span>
      </span>
    );
  }
  const allConfigured = health.checks.oidc && health.checks.anthropic;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={cn(
          "h-2 w-2 rounded-full",
          allConfigured ? "bg-emerald-500" : "bg-amber-500",
        )}
      />
      <span className="hidden md:inline">
        {health.env} • OIDC{health.checks.oidc ? "✓" : "—"} Claude
        {health.checks.anthropic ? "✓" : "—"}
      </span>
    </span>
  );
}

function UserBadge({
  loading,
  user,
  onLogin,
  onLogout,
}: {
  loading: boolean;
  user: ReturnType<typeof useAuth>["user"];
  onLogin: () => void;
  onLogout: () => void;
}) {
  if (loading) {
    return <span className="text-xs text-muted-foreground">…</span>;
  }
  if (!user) {
    return (
      <button
        type="button"
        onClick={onLogin}
        className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
      >
        <i className="fa-solid fa-right-to-bracket" />
        Login
      </button>
    );
  }
  const initials = (user.name || user.email || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");
  return (
    <div className="flex items-center gap-2">
      <span className="hidden md:flex flex-col text-right leading-tight">
        <span className="font-medium text-foreground">{user.name}</span>
        <span className="text-[10px] uppercase tracking-wider">{user.role}</span>
      </span>
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold">
        {initials || <i className="fa-solid fa-user" />}
      </span>
      <button
        type="button"
        onClick={onLogout}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-secondary"
        title="Logout"
      >
        <i className="fa-solid fa-right-from-bracket" />
      </button>
    </div>
  );
}

function LoginScreen({ onLogin, onAbout }: { onLogin: () => void; onAbout: () => void }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Login — links, plain weißer Hintergrund */}
      <div className="flex items-center justify-center p-12 relative">
        <div className="w-full max-w-sm">
          <div className="relative flex items-start justify-center mb-3">
            <img
              src="/logo-fresenius-umwelttechnik.png"
              alt="Fresenius Umwelttechnik"
              className="h-20 w-auto select-none"
              draggable={false}
            />
            <button
              type="button"
              onClick={onAbout}
              className="absolute top-0 -right-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-muted text-muted-foreground text-[10px] hover:bg-secondary transition-colors"
              aria-label="About"
              title="About"
            >
              <i className="fa-solid fa-info" />
            </button>
          </div>
          <div className="text-center text-lg font-medium text-foreground mb-3">
            Data-Tool
          </div>
          <p className="text-center text-sm text-muted-foreground mb-10">
            Bitte melden Sie sich an, um auf das Tool zuzugreifen.
          </p>
          <button
            type="button"
            onClick={onLogin}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <i className="fa-solid fa-right-to-bracket" />
            Mit Fresenius SSO anmelden
          </button>
          <p className="mt-6 text-center text-xs text-muted-foreground">
            <i className="fa-solid fa-shield-halved mr-1.5 text-primary/70" />
            Sicherer SSO-Login
          </p>
        </div>
      </div>

      {/* Hero — rechts, plain teal */}
      <div className="hidden lg:flex items-center justify-start p-16 bg-primary text-primary-foreground">
        <div className="max-w-lg">
          <h1 className="text-4xl font-bold tracking-tight leading-tight mb-6">
            Willkommen im Data-Tool
          </h1>
          <p className="text-primary-foreground/90 text-base leading-relaxed mb-8">
            Erstellen Sie produktgenaue Datenblätter im Fresenius-Design für jede Sprache —
            mit KI-gestützter Übersetzung und automatischer Layout-Anpassung.
          </p>
          <ul className="space-y-3 text-base">
            <FeatureItem>Generieren Sie Datenblätter in 12+ Sprachen</FeatureItem>
            <FeatureItem>KI-gestützte Übersetzung via Claude Haiku</FeatureItem>
            <FeatureItem>Glossar für Marken, Formeln und Normen</FeatureItem>
            <FeatureItem>Audit-Log für jede Mutation & jeden Export</FeatureItem>
          </ul>
        </div>
      </div>
    </div>
  );
}

function FeatureItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <i className="fa-solid fa-check mt-1 text-primary-foreground/90" />
      <span>{children}</span>
    </li>
  );
}

function PhasePlaceholder({ spec }: { spec: TabSpec }) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary mb-4">
        <i className={cn("fa-solid", spec.icon, "text-xl")} />
      </div>
      <h1 className="text-2xl font-semibold tracking-tight mb-1">{spec.label}</h1>
      <p className="text-muted-foreground mb-2">{spec.description}</p>
      <p className="text-xs uppercase tracking-wider text-muted-foreground/70">
        {spec.phase} • noch nicht implementiert
      </p>
    </div>
  );
}

function AboutModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-[min(420px,90vw)] rounded-xl border border-border bg-card p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <i className="fa-solid fa-database" />
          </span>
          <div>
            <div className="font-semibold text-base">Fresenius Data-Tool</div>
            <div className="text-xs text-muted-foreground">data.freseniusinstruments.com</div>
          </div>
        </div>
        <dl className="grid grid-cols-[7rem_1fr] gap-y-2 text-sm">
          <dt className="text-muted-foreground">Author</dt>
          <dd className="font-medium">Alexander Scharff</dd>
          <dt className="text-muted-foreground">Version</dt>
          <dd className="font-mono text-xs">0.2.0 — Phase 2 (OIDC + RBAC)</dd>
          <dt className="text-muted-foreground">Stack</dt>
          <dd>Vite + React + shadcn + Tailwind, Node/Express, PyMuPDF, Authentik OIDC, Claude Haiku</dd>
          <dt className="text-muted-foreground">Copyright</dt>
          <dd>© 2026 Fresenius Umwelttechnik GmbH</dd>
        </dl>
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-sm font-medium bg-secondary hover:bg-secondary/80"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}
